import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import rawOddsService from '../../utils/services/rawOddsService';
import resultsTrackerService from '../../utils/services/resultsTrackerService';
import RawOddsTrackerModal from './RawOddsTrackerModal';
import PriceMovementsModal from './PriceMovementsModal';
import Toast from '../Toast/Toast';

import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import bet365Logo from '../../assets/bet365.png';
import betmakersLogo from '../../assets/betmakers.png';
import ladbrokesLogo from '../../assets/ladbrokes.webp';
import dabbleLogo from '../../assets/dabble.webp';
import tabtouchLogo from '../../assets/tabtouch.webp';
import unibetLogo from '../../assets/unibet.webp';

const SPORT_OPTIONS = [
    { value: 'afl', label: 'AFL' },
];

const MARKET_OPTIONS = [
    { value: 'disposals', label: 'Disposals' },
    { value: 'goals', label: 'Goals' },
    { value: 'marks', label: 'Marks' },
    { value: 'tackles', label: 'Tackles' },
    { value: 'fantasy_points', label: 'Fantasy Points' },
];

const BOOKMAKER_LOGOS = {
    'sportsbet': sportsbetLogo,
    'tab': tabLogo,
    'pointsbet': pointsbetLogo,
    'neds': nedsLogo,
    'betright': betrightLogo,
    'bet365': bet365Logo,
    'betmakers': betmakersLogo,
    'ladbrokes': ladbrokesLogo,
    'dabble': dabbleLogo,
    'tabtouch': tabtouchLogo,
    'unibet': unibetLogo,
};

const BOOKMAKER_DISPLAY_NAMES = {
    'sportsbet': 'Sportsbet',
    'tab': 'TAB',
    'pointsbet': 'Pointsbet',
    'neds': 'Neds',
    'betright': 'Betright',
    'bet365': 'Bet365',
    'betmakers': 'Betmakers',
    'ladbrokes': 'Ladbrokes',
    'unibet': 'Unibet',
    'betr': 'Betr',
    'dabble': 'Dabble',
    'tabtouch': 'TabTouch',
};

const KNOWN_BOOKMAKERS = [
    'sportsbet', 'bet365', 'tab', 'tabtouch', 'pointsbet',
    'betright', 'betmakers', 'neds', 'ladbrokes', 'dabble',
    'unibet', 'betr', 'bluebet',
];

const LINKED_BOOKMAKERS = {
    'tabtouch': 'unibet',
    'neds': 'ladbrokes',
};

function formatStartTime(isoString) {
    const date = new Date(isoString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = date.toDateString() === tomorrow.toDateString();

    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isToday) return `Today ${timeStr}`;
    if (isTomorrow) return `Tomorrow ${timeStr}`;

    const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    return `${dateStr} ${timeStr}`;
}

function formatOdds(odds) {
    if (odds === null || odds === undefined) return '-';
    return Number(odds).toFixed(2);
}

function BookmakerLogo({ bookmaker, size = 20 }) {
    const logo = BOOKMAKER_LOGOS[bookmaker];
    const displayName = BOOKMAKER_DISPLAY_NAMES[bookmaker] || bookmaker;

    if (logo) {
        return (
            <img
                src={logo}
                alt={displayName}
                style={{ width: size, height: size }}
                className="rounded-sm object-cover flex-shrink-0"
                title={displayName}
            />
        );
    }
    return (
        <span
            className="text-[9px] font-mono text-slate-500 uppercase tracking-wider"
            title={displayName}
        >
            {displayName.substring(0, 3)}
        </span>
    );
}

function LiveDot() {
    return (
        <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-40" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
    );
}

function CountdownRing({ countdown, total }) {
    const progress = ((total - countdown) / total) * 100;
    const circumference = 2 * Math.PI * 11;
    const strokeOffset = circumference - (progress / 100) * circumference;

    return (
        <div className="relative w-8 h-8 flex items-center justify-center">
            <svg className="w-8 h-8 -rotate-90" viewBox="0 0 24 24">
                <circle
                    cx="12" cy="12" r="11"
                    fill="none" stroke="currentColor" strokeWidth="1"
                    className="text-slate-800"
                />
                <circle
                    cx="12" cy="12" r="11"
                    fill="none" stroke="currentColor" strokeWidth="1.5"
                    className="text-emerald-500 transition-all duration-1000 ease-linear"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeOffset}
                    strokeLinecap="round"
                />
            </svg>
            <span className="absolute text-[9px] font-mono font-medium text-emerald-500/80">
                {countdown}
            </span>
        </div>
    );
}

function MarketPill({ label, value, selected, onClick }) {
    return (
        <button
            onClick={() => onClick(value)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-all duration-150 ${
                selected
                    ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                    : 'text-slate-400 hover:text-slate-200 border border-transparent hover:border-slate-700'
            }`}
        >
            {label}
        </button>
    );
}

const REFRESH_INTERVAL = 60;

function OddsScreen() {
    const [sport, setSport] = useState('afl');
    const [market, setMarket] = useState('disposals');
    const [oddsData, setOddsData] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
    const [trackerModalEntry, setTrackerModalEntry] = useState(null);
    const [trendsEntry, setTrendsEntry] = useState(null);
    const [isAddingBet, setIsAddingBet] = useState(false);
    const [unitSize, setUnitSize] = useState(20);
    const [toast, setToast] = useState(null);

    const countdownRef = useRef(null);
    const refreshIntervalRef = useRef(null);

    const fetchRawOdds = useCallback(async (selectedSport, selectedMarket, showLoading = false) => {
        if (showLoading) setIsLoading(true);
        setError(null);

        const result = await rawOddsService.getRawOdds(selectedSport, selectedMarket);

        if (result.success) {
            const data = result.data;
            setOddsData(data.odds || []);
            setTotalCount(data.count || 0);
        } else {
            setOddsData(null);
            setTotalCount(0);
            setError(result);

            if (result.isSubscriptionError || result.isAuthError) {
                if (countdownRef.current) clearInterval(countdownRef.current);
                if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
            }
        }

        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchRawOdds(sport, market, true);
        setCountdown(REFRESH_INTERVAL);

        if (countdownRef.current) clearInterval(countdownRef.current);
        if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

        countdownRef.current = setInterval(() => {
            setCountdown(prev => (prev <= 1 ? REFRESH_INTERVAL : prev - 1));
        }, 1000);

        refreshIntervalRef.current = setInterval(() => {
            fetchRawOdds(sport, market);
            setCountdown(REFRESH_INTERVAL);
        }, REFRESH_INTERVAL * 1000);

        return () => {
            clearInterval(countdownRef.current);
            clearInterval(refreshIntervalRef.current);
        };
    }, [sport, market, fetchRawOdds]);

    useEffect(() => {
        const fetchSettings = async () => {
            const result = await resultsTrackerService.getSettings();
            if (result.success && result.data) {
                const { advancedUnitSettings, monthlyUnits, unitSize: defaultUnit } = result.data;
                if (advancedUnitSettings && monthlyUnits?.length > 0) {
                    const now = new Date();
                    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
                    const match = monthlyUnits
                        .filter(m => m.startDate?.substring(0, 7) <= currentMonth)
                        .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
                    setUnitSize(match?.unitSize || defaultUnit || 20);
                } else {
                    setUnitSize(defaultUnit || 20);
                }
            }
        };
        fetchSettings();
    }, []);

    const showToast = useCallback((message, type = 'error', duration = 5000) => {
        setToast({ message, type, duration });
    }, []);

    const handleAddToTracker = useCallback(async (betPayload) => {
        setIsAddingBet(true);
        const result = await resultsTrackerService.addBet(betPayload);
        if (result.success) {
            showToast('Bet added to tracker successfully', 'success');
            setTrackerModalEntry(null);
        } else {
            if (result.isAuthError) {
                showToast('Session expired. Please log in again.', 'error');
            } else {
                showToast(result.message || 'Failed to add bet to tracker', 'error');
            }
        }
        setIsAddingBet(false);
        return result;
    }, [showToast]);

    const handleSportChange = (e) => {
        setSport(e.target.value);
        setSearchTerm('');
    };

    const handleMarketChange = (val) => {
        setMarket(val);
    };

    const allBookmakers = useMemo(() => {
        if (!oddsData || oddsData.length === 0) return [];
        const bookmakerSet = new Set();
        oddsData.forEach(entry => {
            Object.keys(entry.odds).forEach(bk => bookmakerSet.add(bk));
        });
        return KNOWN_BOOKMAKERS.filter(b => bookmakerSet.has(b)).concat(
            [...bookmakerSet].filter(b => !KNOWN_BOOKMAKERS.includes(b)).sort()
        );
    }, [oddsData]);

    const filteredData = useMemo(() => {
        if (!oddsData) return [];
        if (!searchTerm.trim()) return oddsData;
        const term = searchTerm.toLowerCase();
        return oddsData.filter(entry =>
            entry.player.toLowerCase().includes(term) ||
            entry.match.toLowerCase().includes(term) ||
            entry.market.toLowerCase().includes(term)
        );
    }, [oddsData, searchTerm]);

    const groupedByMatch = useMemo(() => {
        const groups = {};
        filteredData.forEach(entry => {
            const matchKey = entry.match;
            if (!groups[matchKey]) {
                groups[matchKey] = {
                    match: entry.match,
                    startTime: entry.startTime,
                    players: {},
                };
            }
            const playerKey = entry.player;
            if (!groups[matchKey].players[playerKey]) {
                groups[matchKey].players[playerKey] = {
                    player: entry.player,
                    lines: [],
                };
            }
            groups[matchKey].players[playerKey].lines.push(entry);
        });

        const parseMarketNumber = (m) => {
            const match = m.match(/(\d+)/);
            return match ? parseInt(match[1], 10) : 0;
        };

        return Object.values(groups)
            .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
            .map(group => ({
                ...group,
                players: Object.values(group.players)
                    .sort((a, b) => a.player.localeCompare(b.player))
                    .map(p => ({
                        ...p,
                        lines: [...p.lines].sort((a, b) => parseMarketNumber(a.market) - parseMarketNumber(b.market)),
                    })),
            }));
    }, [filteredData]);

    const renderEmptyState = () => (
        <div className="flex flex-col items-center justify-center py-24 px-6">
            <div className="w-12 h-12 rounded-lg bg-slate-800/80 border border-slate-700/50 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
            </div>
            <p className="text-sm font-medium text-slate-400 mb-1">No props found</p>
            <p className="text-xs text-slate-600">
                {searchTerm ? 'Try a different search term' : 'No odds data available for this market'}
            </p>
        </div>
    );

    const renderTable = () => {
        if (isLoading && !oddsData) return null;
        if (!oddsData || filteredData.length === 0) {
            if (error) return null;
            return renderEmptyState();
        }

        return (
            <>
                <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-800/90 backdrop-blur-md border-b border-slate-700/50">
                        <th className="text-left px-3 py-3 text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                            Prop
                        </th>
                        <th className="text-center px-3 py-3 whitespace-nowrap">
                            <span className="text-[10px] font-mono font-bold text-emerald-400 uppercase tracking-widest">Best</span>
                        </th>
                        {allBookmakers.map(bk => {
                            const linked = LINKED_BOOKMAKERS[bk];
                            return (
                                <th key={bk} className="text-center px-2 py-3 whitespace-nowrap">
                                    <div className="flex items-center justify-center gap-1">
                                        <BookmakerLogo bookmaker={bk} size={20} />
                                        {linked && (
                                            <>
                                                <span className="text-slate-700 text-[8px]">/</span>
                                                <BookmakerLogo bookmaker={linked} size={20} />
                                            </>
                                        )}
                                    </div>
                                </th>
                            );
                        })}
                        <th className="px-3 py-3 w-[100px]" />
                    </tr>
                </thead>
                <tbody>
                    {groupedByMatch.map((group, groupIdx) => (
                        <React.Fragment key={group.match}>
                            {/* Match header */}
                            <tr className={`bg-slate-950/60 ${groupIdx > 0 ? 'border-t border-slate-700/30' : ''}`}>
                                <td colSpan={3 + allBookmakers.length} className="px-3 pt-5 pb-1">
                                    <div className="flex items-center gap-3">
                                        <span className="text-[13px] font-semibold text-white tracking-tight">
                                            {group.match}
                                        </span>
                                        <span className="text-[11px] font-mono text-slate-600">
                                            {formatStartTime(group.startTime)}
                                        </span>
                                    </div>
                                </td>
                            </tr>
                            {group.players.map((playerGroup) => (
                                <React.Fragment key={`${group.match}-${playerGroup.player}`}>
                                    <tr className="bg-slate-950/40">
                                        <td colSpan={3 + allBookmakers.length} className="px-3 py-2">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-xs font-semibold text-slate-300 leading-none">
                                                    {playerGroup.player}
                                                </span>
                                                <button
                                                    onClick={() => setTrendsEntry({
                                                        player: playerGroup.player,
                                                        match: group.match,
                                                        startTime: group.startTime,
                                                    })}
                                                    title={`View ${playerGroup.player}'s price fluctuations`}
                                                    aria-label={`View price fluctuations for ${playerGroup.player}`}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium font-mono text-emerald-400 bg-emerald-500/5 border border-emerald-500/30 rounded hover:bg-emerald-500/15 hover:border-emerald-500/50 hover:text-emerald-300 transition-all duration-150 whitespace-nowrap leading-none"
                                                >
                                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.75" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
                                                    </svg>
                                                    View Flucts
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {playerGroup.lines.map((entry, idx) => {
                                        const oddsEntries = Object.entries(entry.odds);
                                        const bestOdds = oddsEntries.length > 0
                                            ? Math.max(...oddsEntries.map(([, v]) => v))
                                            : null;
                                        const bestBookmakers = oddsEntries
                                            .filter(([, v]) => v === bestOdds)
                                            .map(([k]) => k);

                                        return (
                                            <tr
                                                key={`${entry.player}-${entry.market}-${idx}`}
                                                className="row-reveal group hover:bg-slate-800/20 transition-colors duration-100"
                                                style={{ animationDelay: `${idx * 30}ms` }}
                                            >
                                                <td className="px-3 pl-6 py-1.5 whitespace-nowrap">
                                                    <span className="text-[12px] font-mono text-slate-500">
                                                        {entry.market}
                                                    </span>
                                                </td>
                                                <td className="text-center px-3 py-1.5 whitespace-nowrap">
                                                    {bestOdds ? (
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <span className="text-[13px] font-mono font-bold text-emerald-400">
                                                                {formatOdds(bestOdds)}
                                                            </span>
                                                            <div className="flex items-center gap-0.5">
                                                                {bestBookmakers.flatMap(bk => {
                                                                    const linked = LINKED_BOOKMAKERS[bk];
                                                                    return linked
                                                                        ? [
                                                                            <BookmakerLogo key={bk} bookmaker={bk} size={14} />,
                                                                            <BookmakerLogo key={linked} bookmaker={linked} size={14} />,
                                                                        ]
                                                                        : [<BookmakerLogo key={bk} bookmaker={bk} size={14} />];
                                                                })}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-800 font-mono">-</span>
                                                    )}
                                                </td>
                                                {allBookmakers.map(bk => {
                                                    const price = entry.odds[bk];
                                                    const isBest = price != null && price === bestOdds;
                                                    return (
                                                        <td
                                                            key={bk}
                                                            className={`text-center px-2 py-1.5 whitespace-nowrap odds-cell ${
                                                                isBest ? 'bg-emerald-500/10' : ''
                                                            }`}
                                                        >
                                                            {price != null ? (
                                                                <span
                                                                    className={`text-[13px] font-mono font-medium tabular-nums ${
                                                                        isBest
                                                                            ? 'text-emerald-400 font-semibold'
                                                                            : 'text-slate-400'
                                                                    }`}
                                                                >
                                                                    {formatOdds(price)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-800 font-mono text-[13px]">
                                                                    —
                                                                </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                                                    <button
                                                        onClick={() => setTrackerModalEntry(entry)}
                                                        className="px-2.5 py-1 text-[11px] font-medium font-mono text-emerald-400 border border-emerald-500/20 rounded hover:bg-emerald-500/10 transition-all duration-150 whitespace-nowrap"
                                                    >
                                                        + Track
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </>
        );
    };

    const renderErrorState = () => {
        if (!error || isLoading) return null;

        if (error.isSubscriptionError) {
            return (
                <div className="flex items-center justify-center py-24 px-6">
                    <div className="flex flex-col items-center text-center max-w-sm">
                        <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-5">
                            <svg className="w-7 h-7 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                        </div>
                        <h4 className="text-base font-semibold font-mono text-white mb-2">Subscription Required</h4>
                        <p className="text-sm font-mono text-slate-500 leading-relaxed mb-3">
                            A Snipe Pro or Snipe Elite subscription is required to access the Odds Screen.
                        </p>
                        <p className="text-xs text-slate-600 font-mono mb-6">
                            Note: Trial subscriptions do not include access to this page.
                        </p>
                        <a
                            href="/manage-subscriptions"
                            className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-semibold hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center gap-2"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            Manage Subscriptions
                        </a>
                    </div>
                </div>
            );
        }

        const errorConfig = {
            AUTH: { icon: 'M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636', color: 'text-red-400', bgColor: 'bg-red-500/5 border-red-500/10', title: 'Authentication Failed' },
            RATE_LIMIT_EXCEEDED: { icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-amber-400', bgColor: 'bg-amber-500/5 border-amber-500/10', title: 'Too Many Requests' },
            SERVICE_UNAVAILABLE: { icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-slate-400', bgColor: 'bg-slate-500/5 border-slate-500/10', title: 'Service Unavailable' },
            NO_DATA: { icon: 'M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-slate-400', bgColor: 'bg-slate-500/5 border-slate-500/10', title: 'No Data Available' },
            NETWORK_ERROR: { icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-red-400', bgColor: 'bg-red-500/5 border-red-500/10', title: 'Connection Error' },
        };

        const code = error.isAuthError ? 'AUTH' : (error.errorCode || 'NETWORK_ERROR');
        const config = errorConfig[code] || errorConfig.NETWORK_ERROR;
        const showRetry = ['SERVICE_UNAVAILABLE', 'NO_DATA', 'NETWORK_ERROR', 'SERVER_ERROR'].includes(error.errorCode);

        return (
            <div className="flex items-center justify-center py-24 px-6">
                <div className="flex flex-col items-center text-center max-w-sm">
                    <div className={`w-12 h-12 rounded-lg ${config.bgColor} border flex items-center justify-center mb-4`}>
                        <svg className={`w-6 h-6 ${config.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d={config.icon} />
                        </svg>
                    </div>
                    <h4 className={`text-sm font-semibold mb-1.5 ${config.color}`}>
                        {config.title}
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed">{error.message}</p>
                    {showRetry && (
                        <button
                            onClick={() => { fetchRawOdds(sport, market, true); setCountdown(REFRESH_INTERVAL); }}
                            className="mt-5 px-4 py-2 text-xs font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all flex items-center gap-2"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Retry
                        </button>
                    )}
                    {error.errorCode === 'RATE_LIMIT_EXCEEDED' && (
                        <p className="mt-3 text-[10px] font-mono text-slate-700">Resets every 60s</p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="flex flex-col p-2 md:p-4 h-[calc(100vh-72px)] overflow-hidden">
            <div className="bg-slate-900/50 rounded-xl md:rounded-2xl border border-slate-700/50 overflow-hidden relative flex flex-col flex-1 min-h-0">

                {/* Initial loading overlay */}
                {isLoading && !oddsData && (
                    <div className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm z-10 flex items-center justify-center">
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex gap-1">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '0ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '200ms' }} />
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse-dot" style={{ animationDelay: '400ms' }} />
                            </div>
                            <span className="text-xs font-mono text-slate-500">Loading odds data</span>
                        </div>
                    </div>
                )}

                {/* Refresh/switch loading overlay */}
                {isLoading && oddsData && (
                    <div className="absolute inset-0 bg-slate-900/40 z-10 flex items-center justify-center pointer-events-none">
                        <div className="flex items-center gap-2.5 bg-slate-800 px-4 py-2.5 rounded-lg border border-slate-700/50 shadow-xl pointer-events-auto">
                            <div className="w-4 h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                            <span className="text-xs font-mono text-slate-400">Updating...</span>
                        </div>
                    </div>
                )}

                {/* Command Bar Header */}
                <div className="border-b border-slate-700/50">
                    {/* Desktop Header */}
                    <div className="hidden md:flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2.5">
                                <h3 className="text-sm font-semibold text-white tracking-tight">Odds Screen</h3>
                                {!isLoading && oddsData && (
                                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-800/50 rounded">
                                        <span className="text-[11px] font-mono font-semibold text-white">{totalCount}</span>
                                        <span className="text-[11px] font-mono text-slate-600">props</span>
                                    </div>
                                )}
                            </div>

                            <div className="w-px h-5 bg-slate-700/50" />

                            <select
                                value={sport}
                                onChange={handleSportChange}
                                className="px-2.5 py-1 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-slate-300 focus:outline-none focus:border-emerald-500 cursor-pointer hover:border-slate-600 transition-colors"
                            >
                                {SPORT_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>

                            <div className="flex items-center gap-1">
                                {MARKET_OPTIONS.map(opt => (
                                    <MarketPill
                                        key={opt.value}
                                        label={opt.label}
                                        value={opt.value}
                                        selected={market === opt.value}
                                        onClick={handleMarketChange}
                                    />
                                ))}
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-8 pr-3 py-1.5 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-48 hover:border-slate-600 transition-colors"
                                />
                            </div>

                            {!isLoading && !error && (
                                <div className="flex items-center gap-2">
                                    <LiveDot />
                                    <CountdownRing countdown={countdown} total={REFRESH_INTERVAL} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile Header */}
                    <div className="flex md:hidden items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-white">Odds Screen</h3>
                            {!isLoading && oddsData && (
                                <span className="text-[11px] font-mono text-slate-600">
                                    <span className="text-slate-400 font-semibold">{totalCount}</span> props
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            {!isLoading && !error && (
                                <CountdownRing countdown={countdown} total={REFRESH_INTERVAL} />
                            )}
                            <button
                                onClick={() => setMobileSettingsOpen(prev => !prev)}
                                className="p-1.5 border border-slate-700 rounded text-slate-500 hover:text-white hover:border-slate-600 transition-colors"
                            >
                                <svg className={`w-4 h-4 transition-transform duration-200 ${mobileSettingsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Mobile Collapsible Controls */}
                    {mobileSettingsOpen && (
                        <div className="md:hidden px-3 pb-3 border-t border-slate-700/50 pt-3 animate-slide-up space-y-3">
                            <div className="flex gap-2">
                                <select
                                    value={sport}
                                    onChange={handleSportChange}
                                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {SPORT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                                <select
                                    value={market}
                                    onChange={(e) => handleMarketChange(e.target.value)}
                                    className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white focus:outline-none focus:border-emerald-500"
                                >
                                    {MARKET_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="relative">
                                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                                <input
                                    type="text"
                                    placeholder="Search player, market or match..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full"
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Error State */}
                {renderErrorState()}

                {/* Table */}
                {!error && (
                    <div className="overflow-auto flex-1 min-h-0 styled-scrollbar">
                        <table className="w-full table-auto">
                            {renderTable()}
                        </table>
                    </div>
                )}

                {/* Status Footer */}
                {!error && !isLoading && oddsData && (
                    <div className="px-4 py-2.5 border-t border-slate-700/50 flex items-center justify-between">
                        <div className="text-[11px] font-mono text-slate-600">
                            <span className="text-slate-500">{filteredData.length}</span>
                            {filteredData.length !== totalCount && (
                                <span className="text-slate-700"> / {totalCount}</span>
                            )}
                            <span className="ml-1">props</span>
                        </div>
                        <div className="text-[10px] font-mono text-slate-700">
                            {MARKET_OPTIONS.find(m => m.value === market)?.label} · {SPORT_OPTIONS.find(s => s.value === sport)?.label}
                        </div>
                    </div>
                )}
            </div>

            {trackerModalEntry && (
                <RawOddsTrackerModal
                    entry={trackerModalEntry}
                    onClose={() => setTrackerModalEntry(null)}
                    bookmakerLogos={BOOKMAKER_LOGOS}
                    unitSize={unitSize}
                    onAddToTracker={handleAddToTracker}
                    isAddingBet={isAddingBet}
                />
            )}

            <PriceMovementsModal
                open={Boolean(trendsEntry)}
                player={trendsEntry?.player}
                propType={market}
                initialMarket={trendsEntry?.market}
                matchLabel={trendsEntry?.match}
                startTime={trendsEntry?.startTime}
                sport={sport}
                onClose={() => setTrendsEntry(null)}
                onPropTypeChange={(next) => setMarket(next)}
                onAuthError={(err) => {
                    showToast(err?.message || 'Session expired. Please log in again.', 'error');
                }}
            />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
}

export default OddsScreen;
